'use client';

import { useEffect, useRef } from 'react';

import { Box, Container } from '@mui/material';
import cx from 'clsx';
import NextImage from 'next/image';

import { Image } from '@/types/image.type';
import { Video } from '@/types/video.type';
import useIntersectionObserver from '@/utils/hooks/useIntersectionObserver';
import { isVideo } from '@/utils/static/isVideo';

import styles from './GallerySection.module.scss';

interface GallerySectionProps {
  gallery: Image[] | Video[];
}

interface GalleryItemProps {
  item: Image | Video;
  index: number;
}

const GalleryItem = ({ item, index }: GalleryItemProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemIsVideo = isVideo(item);

  const isInViewport = useIntersectionObserver(containerRef, {
    threshold: 0.2,
    triggerOnce: false,
    rootMargin: '100px 0px',
  });

  useEffect(() => {
    if (!itemIsVideo || !videoRef.current) return undefined;

    const videoElement = videoRef.current;
    let canPlayHandler: (() => void) | null = null;

    if (isInViewport) {
      const playPromise = videoElement.play();

      if (playPromise !== undefined) {
        playPromise.catch(() => {
          canPlayHandler = () => {
            videoElement.play().catch(() => {});
          };

          videoElement.addEventListener('canplay', canPlayHandler, { once: true });
        });
      }
    } else {
      videoElement.pause();
    }

    return () => {
      if (canPlayHandler) {
        videoElement.removeEventListener('canplay', canPlayHandler);
      }
    };
  }, [isInViewport, itemIsVideo]);

  return (
    <Box
      ref={containerRef}
      key={item.src}
      className={cx(styles.image, {
        [styles.fullWidthImage]: index === 0,
        [styles.halfWidthImage]: index % 3 !== 0,
        [styles.lastImage]: index === 3,
      })}
    >
      {itemIsVideo ? (
        <video ref={videoRef} muted loop playsInline preload="metadata" aria-label={item.alt} className={styles.video}>
          <source src={item.src} type="video/mp4" />
        </video>
      ) : (
        <NextImage src={item.src} alt={item.alt} fill style={{ objectFit: 'cover' }} unoptimized />
      )}
    </Box>
  );
};

const GallerySection = ({ gallery }: GallerySectionProps) => (
  <Container component="section" maxWidth="xl" className={styles.container}>
    <Box className={styles.grid}>
      {gallery.map((item, index) => (
        <GalleryItem key={item.src} item={item} index={index} />
      ))}
    </Box>
  </Container>
);

export default GallerySection;
